/**
 * Albums as a first-class screen.
 *
 * Groups were one tap away and albums were buried in a dropdown, so people
 * managed groups and ignored albums. This gives albums the same treatment the
 * group list gets: real cards, cover images, counts, and a create action that
 * is visible without hunting.
 */

import { useMemo, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Album } from "./types.js";
import { trpcClient } from "../../lib/trpc.js";
import { imageAccept } from "../../lib/imageSupport.js";
import { fetchSignedUrls } from "../../lib/signedUrls.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { AlbumDialog } from "./AlbumDialog.js";
import { SaveToPhone } from "./SaveToPhone.js";
import { trpc } from "../../lib/trpc.js";
import { useDecryptedUrl, type MediaRef } from "../../lib/useDecryptedUrl.js";
import "./AlbumsView.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";
import { GroupBar } from "../group/GroupBar.js";
import { flagFor, primaryCountry } from "../../lib/countries.js";

export interface AlbumsViewProps {
  groupId: string;
  folders: Album[];
  canCreate: boolean;
  isLoading: boolean;
  onChanged: () => void;
}

export function AlbumsView({
  groupId,
  folders,
  canCreate,
  isLoading,
  onChanged,
}: AlbumsViewProps) {
  const t = useT();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<Album | null | "new">(null);
  const [saving, setSaving] = useState<Album | null>(null);

  const albums = useMemo(
    () => folders.filter((folder) => folder.kind === "trip"),
    [folders],
  );

  /**
   * Cover thumbnails, signed in one batch.
   *
   * Only albums that have an explicit cover are requested — falling back to
   * "the newest photo in the album" would mean a query per album just to draw
   * a card.
   */
  const coverIds = useMemo(
    () =>
      albums
        .map((album) => album.coverFileId)
        .filter((id): id is string => Boolean(id)),
    [albums],
  );

  const covers = useQuery({
    queryKey: ["albumCovers", groupId, coverIds],
    enabled: coverIds.length > 0,
    staleTime: 50 * 60_000,
    // Chunked: one cover per album, and nothing caps how many albums a group
    // may have.
    queryFn: async () => ({
      urls: await fetchSignedUrls(
        groupId,
        coverIds,
        "thumb",
        await imageAccept(),
      ),
    }),
  });

  const coverRef = (fileId: string | null): MediaRef | null => {
    if (!fileId) return null;
    const entry = covers.data?.urls.find((row) => row.fileId === fileId);
    if (!entry?.url) return null;
    return { url: entry.url, wrappedFileKey: entry.wrappedFileKey ?? null };
  };

  const openAlbum = (folderId: string) => {
    void navigate({
      to: "/g/$groupId",
      params: { groupId },
      search: { folder: folderId },
    });
  };

  if (isLoading) {
    return (
      <div className="fc-albums">
        <div className="fc-albums__grid">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} height="180px" radius="var(--fc-radius-lg)" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="fc-albums">
      <header className="fc-albums__header">
        <div>
          {/* This screen said “Albums” and a count and nothing about which group
              they were in — it returns before the gallery header renders. */}
          <GroupBar groupId={groupId} />
          <h1>{t("Albums")}</h1>
          <p>
            {albums.length} {albums.length === 1 ? "album" : "albums"} in this
            group
          </p>
        </div>
        {canCreate && (
          <Button variant="primary" onClick={() => setEditing("new")}>
            {t("New album")}
          </Button>
        )}
      </header>

      {albums.length === 0 ? (
        <EmptyState
          icon={<AlbumIcon />}
          title="No albums yet"
          description="Albums keep a trip together — Barcelona 2026, Tatry 2025. Photos you add to one stay in All photos too."
          action={
            canCreate ? (
              <Button variant="primary" onClick={() => setEditing("new")}>
                Create the first album
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="fc-albums__grid">
          {albums.map((album) => {
            const cover = coverRef(album.coverFileId);
            const total = album.photoCount + album.videoCount;

            return (
              <article
                key={album.id}
                className="fc-album"
                data-accent={album.accentColor ?? undefined}
              >
                <button
                  type="button"
                  className="fc-album__open"
                  onClick={() => openAlbum(album.id)}
                  aria-label={`Open ${album.name}, ${total} items`}
                >
                  <span className="fc-album__cover">
                    <Cover
                      source={cover}
                      groupId={groupId}
                      fallback={
                        album.emoji ??
                        (primaryCountry(album.countryCodes)
                          ? flagFor(primaryCountry(album.countryCodes)!)
                          : "📁")
                      }
                    />
                  </span>

                  <span className="fc-album__body">
                    <span className="fc-album__title">
                      {album.emoji && cover && (
                        <span className="fc-album__emoji" aria-hidden="true">
                          <EmojiIcon emoji={album.emoji} />
                        </span>
                      )}
                      {album.name}
                    </span>
                    <span className="fc-album__meta fc-tabular">
                      {total === 0
                        ? "Empty"
                        : `${album.photoCount} photos · ${album.videoCount} videos`}
                      {album.startDate
                        ? ` · ${formatRange(album.startDate, album.endDate)}`
                        : ""}
                    </span>
                    {album.locationName && (
                      <span className="fc-album__location">
                        {album.locationName}
                      </span>
                    )}
                  </span>
                </button>

                {/* Offered on every album with something in it, but kept as a
                    quiet icon rather than a call to action — it is a useful
                    escape hatch, not something to nag people towards. */}
                {album.photoCount + album.videoCount > 0 && (
                  <button
                    type="button"
                    className="fc-album__save"
                    aria-label={`Save ${album.name} to your phone`}
                    title="Save to your phone"
                    onClick={() => setSaving(album)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5M5 20h14" />
                    </svg>
                  </button>
                )}

                {canCreate && (
                  <button
                    type="button"
                    className="fc-album__edit"
                    aria-label={`Edit ${album.name}`}
                    onClick={() => setEditing(album)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16z" />
                    </svg>
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {saving && (
        <SaveAlbumToPhone
          groupId={groupId}
          album={saving}
          onClose={() => setSaving(null)}
        />
      )}

      {editing && (
        <AlbumDialog
          groupId={groupId}
          folder={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** "12–18 Jun 2026", collapsing the month when both dates share one. */
function formatRange(start: string, end: string | null): string {
  const from = new Date(start);
  if (!end)
    return from.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });

  const to = new Date(end);
  const sameMonth =
    from.getMonth() === to.getMonth() &&
    from.getFullYear() === to.getFullYear();

  return sameMonth
    ? `${from.getDate()}–${to.getDate()} ${to.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`
    : `${from.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${to.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

function AlbumIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M7 6V4.5A1.5 1.5 0 0 1 8.5 3h7A1.5 1.5 0 0 1 17 4.5V6" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

/**
 * Loads what is actually in the album, then offers to save it.
 *
 * Split out so the list of files is fetched only when someone asks to save —
 * an album screen showing twelve albums should not pull twelve file listings
 * on the off-chance.
 */
function SaveAlbumToPhone({
  groupId,
  album,
  onClose,
}: {
  groupId: string;
  album: Album;
  onClose: () => void;
}) {
  const group = useQuery(trpc.groups.get.queryOptions({ groupId }));

  const contents = useQuery({
    queryKey: ["album-contents", groupId, album.id],
    queryFn: () =>
      trpcClient.media.list.query({
        groupId,
        filter: { folderId: album.id },
        limit: 200,
      }),
  });

  if (contents.isLoading || group.isLoading) return null;

  const items = (contents.data?.items ?? [])
    // Nothing to hand over for a file the server never finished processing.
    .filter((item) => item.status === "ready")
    .map((item) => ({
      id: item.id,
      name: item.name,
      sizeBytes: item.sizeBytes,
    }));

  return (
    <SaveToPhone
      groupId={groupId}
      albumName={album.name}
      items={items}
      canUseOriginals={(group.data?.capabilities ?? []).includes(
        "file:download_original",
      )}
      onClose={onClose}
    />
  );
}

/**
 * An album's cover picture.
 *
 * Its own component because the cover has to be decrypted, and a hook cannot
 * run inside the loop that renders the cards. The emoji fallback covers three
 * cases that look the same from here and need no distinction on a card: no
 * cover chosen, one still processing, and one this session cannot open.
 */
function Cover({
  source,
  groupId,
  fallback,
}: {
  source: MediaRef | null;
  groupId: string;
  fallback: string;
}) {
  const { url } = useDecryptedUrl(source, groupId);

  if (!url) {
    return (
      <span className="fc-album__placeholder" aria-hidden="true">
        <EmojiIcon emoji={fallback} />
      </span>
    );
  }

  return <img src={url} alt="" decoding="async" />;
}
